import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { select, zoom } from 'd3'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import appendGroupCirclesBaseUtil from '../../../../../core/helpers/mirror/appendGroupCirclesBaseUtil'
import SvgAuthorGroup from './SvgAuthorGroup'
import AuthorCardConstellation from './AuthorCardConstellation'
import {
  buildRelativePath,
  PathConfig,
} from '../../../../../core/config/PathConfig'
import { AuthorMirrorAuthorInterface } from '../../../../../core/interface/report/author-mirror-author.interface'
import { ConstellationDataInterface } from '../../../../../core/interface/analytics/mirror/ConstellationDataInterface.interface'
import authorMirrorConstellationPositionUtil from '../../../../../core/helpers/mirror/author/author-mirror-constellation-position.util'
import { AuthorInterface } from '../../../../../core/interface/author.interface'
import appendCenterAuthorInputsUtil from '../../../../../core/helpers/mirror/author/appendCenterAuthorInputsUtil'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAnalyticDetailsStateAnalyticDetailsType } from '../../../../../store/getters/analytics-details/analytic-details-v2.getters'

const MARGIN_BOUNCE_MENU = 35

const D3ConstellationAuthorMirror = ({
  authorsInput,
  authorsOutput,
  handleAuthorHovered,
  authorDetailsSelected,
  handleAuthorSelected,
}: {
  authorsInput: AuthorInterface[]
  authorsOutput: AuthorMirrorAuthorInterface[]
  handleAuthorHovered: (authorId: string | null) => void
  authorDetailsSelected: AuthorMirrorAuthorInterface | null
  handleAuthorSelected: (
    authorDetails: AuthorMirrorAuthorInterface | null,
  ) => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const svgRef = useRef<any>(null)
  const svgContainerRef = useRef<any>(null)
  const [booksConstellation, setBooksConstellation] = useState<{
    level1: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
    level2: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
    level3: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
    level4: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
    level5: ConstellationDataInterface<AuthorMirrorAuthorInterface>[]
  }>({
    level1: [],
    level2: [],
    level3: [],
    level4: [],
    level5: [],
  })

  let timer: NodeJS.Timeout | null = null
  const [needClose, setNeedClose] = useState<boolean>(false)
  const [authorDisplay, setAuthorDisplay] =
    useState<ConstellationDataInterface<AuthorMirrorAuthorInterface> | null>(
      null,
    )
  const [menuPositionTop, setMenuPositionTop] = useState<boolean>(false)
  const componentRef = useRef<HTMLDivElement>(null)
  const [heightOfBounceMenu, setHeightOfBounceMenu] = useState<number>(0)

  const analyticsDetailsType = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetailsType,
  )

  const [transform, setTransform] = useState<{
    x: number
    y: number
    k: number
  }>({ x: 0, y: 0, k: 0 })

  const setTransformCb = useCallback(
    (values: { x: number; y: number; k: number }) => {
      setTransform(values)
    },
    [transform],
  )

  useEffect(() => {
    const width = svgRef?.current?.clientWidth
    const height = svgRef?.current?.clientHeight

    setTransformCb({
      x: 0,
      y: 0,
      k: 1,
    })
    const svg = select(svgRef.current)
    const svgContainer = select(svgContainerRef.current)

    const radiusBaseCircle: number[] = [200, 400, 600, 800, 1000]

    appendGroupCirclesBaseUtil(svgContainer, {
      radiusBaseCircle,
      width,
      height,
    })

    if ((authorsInput || []).length && (authorsOutput || []).length) {
      const { level1, level2, level3, level4, level5 } =
        authorMirrorConstellationPositionUtil({
          center: { x: width / 2, y: height / 2 },
          radiusBaseCircle,
          authorsOutput,
        })
      setBooksConstellation({ level1, level2, level3, level4, level5 })
      appendCenterAuthorInputsUtil(svgContainer, authorsInput, {
        width,
        height,
      })
    } else {
      svgContainer
        .selectAll('g')
        .append('circle')
        .attr('cx', width / 2)
        .attr('cy', height / 2)
        .attr('r', 50)
        .attr('fill', 'black')
        .attr('stroke', '#b2b2b233')
    }

    svg.call(
      zoom().on('zoom', (event) => {
        svgContainer.attr('transform', event.transform)
        setTransformCb(event.transform)
      }),
    )

    return () => {}
  }, [authorsInput, authorsOutput])

  const handleDisplayAuthorCard = useCallback(
    (data: ConstellationDataInterface<AuthorMirrorAuthorInterface> | null) => {
      if (data) {
        setAuthorDisplay(data)
        setNeedClose(false)
        handleAuthorHovered(data?.mirror?.author_id)
      } else {
        setNeedClose(true)
      }
    },
    [],
  )

  const handleTimeOut = () => {
    timer = setTimeout(() => {
      setNeedClose(false)
      setAuthorDisplay(null)
      handleAuthorHovered(null)
    }, 500)
  }

  useEffect(() => {
    if (needClose) {
      handleTimeOut()
    }

    if (authorDisplay) {
      const componentHeight = componentRef?.current?.clientHeight as number
      setHeightOfBounceMenu(componentHeight)
      if (
        (transform?.y || 0) +
          (authorDisplay?.y || 0) * (transform?.k || 0) +
          (authorDisplay?.r || 0) * 2 +
          componentHeight +
          MARGIN_BOUNCE_MENU >
          window.innerHeight &&
        !menuPositionTop
      ) {
        setMenuPositionTop(true)
      } else if (
        +(transform?.y || 0) +
          +(authorDisplay?.y || 0) * +(transform?.k || 0) +
          (authorDisplay?.r || 0) * 2 +
          componentHeight +
          MARGIN_BOUNCE_MENU <=
          window.innerHeight &&
        menuPositionTop
      ) {
        setMenuPositionTop(false)
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [authorDisplay, needClose])

  const handleOnMouseEnter = () => {
    setNeedClose(false)
  }
  const handleOnMouseLeave = () => {
    setNeedClose(true)
  }

  const handleOnClickBounceToNewReport = (event: React.MouseEvent) => {
    event.preventDefault()
    navigate(
      buildRelativePath([
        PathConfig.BOOKMETRIE,
        PathConfig.ANALYTICS,
        `new?type=${analyticsDetailsType}&authors=${authorDisplay?.mirror?.author_id}`,
      ]),
    )
  }

  return (
    <div
      className="absolute top-0 left-0 w-full h-full flex"
      style={{
        zIndex: 6,
      }}
    >
      <div className="relative w-full h-full">
        {authorDisplay && (
          <div
            ref={componentRef}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
            className={`absolute flex items-center justify-center ${
              menuPositionTop ? 'flex-col-reverse' : 'flex-col'
            }`}
            style={{
              top: menuPositionTop
                ? (transform?.y || 0) +
                  (authorDisplay?.y || 0) * (transform?.k || 0) -
                  authorDisplay.r * 1 -
                  heightOfBounceMenu
                : (transform?.y || 0) +
                  (authorDisplay?.y || 0) * (transform?.k || 0) +
                  authorDisplay.r,
              left:
                (transform?.x || 0) +
                (authorDisplay?.x || 0) * (transform?.k || 0) -
                126,
            }}
          >
            <button
              type="button"
              className="flex flex-row gap-2 rounded p-2 items-center justify-center hover:shadow-xl bg-black text-white"
              onClick={handleOnClickBounceToNewReport}
            >
              {t('commons.buttons.bounce')}
            </button>
            <AuthorCardConstellation data={authorDisplay} />
          </div>
        )}
        <svg
          ref={svgRef}
          className="w-full h-full  bg-white"
          style={{
            borderRadius: 16,
          }}
          preserveAspectRatio="xMidYMid slice"
        >
          <g ref={svgContainerRef}>
            <defs>
              {authorsOutput.map((author) => {
                return (
                  <React.Fragment key={`${author.author_id}-radialGradient`}>
                    <radialGradient
                      id={`radialGradient-${author.author_id}-out`}
                      cx="50%"
                      cy="50%"
                      r="50%"
                    >
                      <stop offset="0.841325" stopColor="white" />
                      <stop offset="1" stopColor="white" stopOpacity="0.5" />
                    </radialGradient>
                    <radialGradient
                      id={`radialGradient-${author.author_id}-in`}
                      cx="50%"
                      cy="50%"
                      r="50%"
                    >
                      <stop offset="0.202484" stopColor="white" />
                      <stop offset="1" stopColor="white" stopOpacity="0.5" />
                    </radialGradient>
                  </React.Fragment>
                )
              })}
            </defs>
            {booksConstellation.level1.map(
              (
                value: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => (
                <SvgAuthorGroup
                  key={`author-node-level1-${value?.mirror?.author_id}`}
                  data={value}
                  edgeCenter={10}
                  svgContainer={select(svgContainerRef.current)}
                  handleDisplayAuthorCard={handleDisplayAuthorCard}
                  authorIsSelected={
                    authorDetailsSelected?.author_id ===
                    value?.mirror?.author_id
                  }
                  handleAuthorSelected={handleAuthorSelected}
                />
              ),
            )}
            {booksConstellation.level2.map(
              (
                value: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => (
                <SvgAuthorGroup
                  key={`author-node-level2-${value?.mirror?.author_id}`}
                  data={value}
                  edgeCenter={4}
                  svgContainer={select(svgContainerRef.current)}
                  handleDisplayAuthorCard={handleDisplayAuthorCard}
                  authorIsSelected={
                    authorDetailsSelected?.author_id ===
                    value?.mirror?.author_id
                  }
                  handleAuthorSelected={handleAuthorSelected}
                />
              ),
            )}
            {booksConstellation.level3.map(
              (
                value: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => (
                <SvgAuthorGroup
                  key={`author-node-level3-${value?.mirror?.author_id}`}
                  data={value}
                  edgeCenter={2}
                  svgContainer={select(svgContainerRef.current)}
                  handleDisplayAuthorCard={handleDisplayAuthorCard}
                  authorIsSelected={
                    authorDetailsSelected?.author_id ===
                    value?.mirror?.author_id
                  }
                  handleAuthorSelected={handleAuthorSelected}
                />
              ),
            )}
            {booksConstellation.level4.map(
              (
                value: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => (
                <SvgAuthorGroup
                  key={`author-node-level4-${value?.mirror?.author_id}`}
                  data={value}
                  svgContainer={select(svgContainerRef.current)}
                  handleDisplayAuthorCard={handleDisplayAuthorCard}
                  authorIsSelected={
                    authorDetailsSelected?.author_id ===
                    value?.mirror?.author_id
                  }
                  handleAuthorSelected={handleAuthorSelected}
                />
              ),
            )}
            {booksConstellation.level5.map(
              (
                value: ConstellationDataInterface<AuthorMirrorAuthorInterface>,
              ) => (
                <SvgAuthorGroup
                  key={`author-node-level5-${value?.mirror?.author_id}`}
                  data={value}
                  svgContainer={select(svgContainerRef.current)}
                  handleDisplayAuthorCard={handleDisplayAuthorCard}
                  authorIsSelected={
                    authorDetailsSelected?.author_id ===
                    value?.mirror?.author_id
                  }
                  handleAuthorSelected={handleAuthorSelected}
                />
              ),
            )}
          </g>
        </svg>
      </div>
    </div>
  )
}

export default memo(D3ConstellationAuthorMirror)
